<template>
  <div class="bg_white ptb-115">
    <div class="container pb-1">
      <div class="row align-items-center mb-45">
        <div class="col-lg-7 col-md-8">
          <h6 class="fs-16 fw-normal text-para mb-12">{{ subtitle }}</h6>
          <h2 class="section-title style-one fw-semibold ls-n2 text-title mb-lg-0 mb-4">
            {{ title }}
          </h2>
        </div>

        <div class="col-lg-5 col-md-4 text-md-end">
          <router-link
            :to="viewAllLink"
            class="btn style-one d-inline-flex flex-wrap align-items-center transition"
          >
            <span
              class="btn-text d-flex flex-column align-items-center justify-content-center transition"
            >
              {{ viewAllText }}
            </span>
            <span
              class="btn-icon d-flex flex-column align-items-center justify-content-center transition"
            >
              <img :src="rightArrowIcon" alt="Icon" />
            </span>
          </router-link>
        </div>
      </div>

      <div
        v-for="(service, index) in services"
        :key="service.id"
        class="case-card style-one z-1 overflow-hidden position-lg-sticky round-20 mb-50"
      >
        <div class="img-container position-absolute top-0 overflow-hidden start-0 w-100 h-100">
          <img 
            :src="service.featured_image" 
            :alt="service.title" 
            class="w-100 h-100 object-fit-cover round-20" 
          />
        </div>
        <span class="case-counter text-white d-block fw-semibold ls-n2">
          {{ formatCounter(index + 1) }}
        </span>
        <div
          class="case-info position-relative z-1 d-flex flex-wrap align-items-center justify-content-between"
        >
          <div>
            <h3 class="section-title style-one fw-semibold ls-n2 mb-15 text-white">
              {{ service.title }}
            </h3>
            <div 
              v-if="service.content" 
              v-html="getPlainText(service.content)"
              class="text-white mb-0 service-description"
            ></div>
            <p v-else-if="service.description" class="text-white mb-0">
              {{ service.description }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Import images
import rightArrowIcon from '@/assets/img/icons/right-arrow.svg'

export default {
    name: 'HomeServices',
    data() {
        return {
            subtitle: 'Our Services',
            title: "Explore our services and the results we've achieved for our clients",
            viewAllText: 'View All Services',
            viewAllLink: '/services',
            services: [],
            rightArrowIcon,
            moveTextItems: [
                'Excellence in service, care, and client success.',
                'Excellence in service, care, and client success.'
            ],
            loading: true
        }
    },
    async mounted() {
        await this.loadServices()
        await this.loadSectionSettings()
        
        // Initialize GSAP animations after services are loaded
        this.$nextTick(() => {
            if (this.services.length > 0) {
                this.initAnimations()
            }
        })
    },
    methods: {
        async loadServices() {
            this.loading = true
            
            try {
                const response = await this.$api.get('public/content/services')
                if (response.data && response.data.data) {
                    this.services = response.data.data
                }
            } catch (error) {
                console.error('Failed to load services:', error)
                this.services = []
            } finally {
                this.loading = false
            }
        },
        
        async loadSectionSettings() {
            try {
                const response = await this.$api.get('public/settings?section=home')
                if (response.data && response.data.status === 'success') {
                    const data = response.data.data
                    
                    if (data.services_subtitle) {
                        this.subtitle = data.services_subtitle
                    }
                    if (data.services_title) {
                        this.title = data.services_title
                    }
                    if (data.services_button_text) {
                        this.viewAllText = data.services_button_text
                    }
                }
            } catch (error) {
                console.error('Failed to load section settings:', error)
            }
        },
        
        formatCounter(num) {
            return num.toString().padStart(2, '0')
        },
        
        getPlainText(html) {
            // Strip HTML tags and get plain text for description
            const temp = document.createElement('div')
            temp.innerHTML = html
            const text = temp.textContent || temp.innerText || ''
            // Limit to first 150 characters
            return text.length > 150 ? text.substring(0, 150) + '...' : text
        },
        
        initAnimations() {
            if (typeof window !== 'undefined' && window.gsap && window.ScrollTrigger) {
                // Parallax image effect
                const containers = document.querySelectorAll('.img-container')
                containers.forEach((container) => {
                    const img = container.querySelector('img')
                    if (!img) return
                    
                    window.gsap.fromTo(
                        img,
                        { yPercent: -20 },
                        {
                            yPercent: 20,
                            ease: 'none',
                            scrollTrigger: { 
                                trigger: container, 
                                scrub: true 
                            }
                        }
                    )
                })
            }
        }
    }
}
</script>

<style scoped>
.case-card.style-one {
    position: relative;
    padding: 60px;
    min-height: 450px;
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5));
    top: 0;
}

.case-card.style-one:nth-child(odd) {
    top: 0;
}

.case-card.style-one:nth-child(even) {
    top: 50px;
}

@media (min-width: 992px) {
    .case-card.style-one {
        position: sticky;
        top: 100px;
    }
}

.img-container {
    z-index: -1;
}

.img-container img {
    transition: transform 0.1s ease-out;
}

.case-counter {
    font-size: 120px;
    line-height: 1;
    opacity: 0.15;
    position: absolute;
    top: 40px;
    right: 60px;
}

.case-info {
    max-width: 600px;
}

.case-info h3 {
    font-size: 36px;
}

.service-description {
    line-height: 1.8;
    font-size: 16px;
}

.move-text.style-two {
    padding: 30px 0;
    background: linear-gradient(90deg, transparent, rgba(var(--bs-primary-rgb), 0.05), transparent);
}

.move-text.style-two ul {
    animation: moveTextHorizontal 20s linear infinite;
    white-space: nowrap;
}

.move-text.style-two li {
    display: inline-block;
    font-size: 24px;
    font-weight: 600;
    margin: 0 80px;
    position: relative;
}

.move-text.style-two li:after {
    content: '★';
    position: absolute;
    right: -50px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 20px;
}

@keyframes moveTextHorizontal {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(-50%);
    }
}

@media (max-width: 991px) {
    .case-card.style-one {
        padding: 40px 30px;
        min-height: 350px;
    }
    
    .case-counter {
        font-size: 80px;
        top: 30px;
        right: 30px;
    }
    
    .case-info h3 {
        font-size: 28px;
    }
    
    .move-text.style-two li {
        font-size: 18px;
        margin: 0 50px;
    }
}

@media (max-width: 767px) {
    .case-card.style-one {
        padding: 30px 20px;
        min-height: 300px;
    }
    
    .case-counter {
        font-size: 60px;
        top: 20px;
        right: 20px;
    }
    
    .case-info h3 {
        font-size: 24px;
    }
}
</style>
